import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  get_department_with_categories,
  get_view_ingredients,
  get_view_units,
  kitchen_raise_item_request,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import Loader from "../components/layout/Loader.js";
import { useToast } from "../components/ui/use-toast";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

/** Chhoti value pe zyada decimals — 60 g in KG = 0.06, "0.00" nahi */
const fmtQty = (v) => {
  const n = num(v);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  let d = 2;
  if (abs < 0.001) d = 6;
  else if (abs < 0.01) d = 5;
  else if (abs < 1) d = 4;
  else if (abs < 100) d = 3;
  return String(parseFloat(n.toFixed(d)));
};

const money = (v) => `₹${num(v).toFixed(2)}`;

const priorities = [
  { value: "high", label: "High Priority" },
  { value: "medium", label: "Medium Priority" },
  { value: "low", label: "Low Priority" },
];

const emptyRow = (id) => ({
  id,
  itemName: "",
  quantity: "",
  unit_id: "",
  ingredient_id: "",
  default_unit_id: "",
  rate_per_base: 0,
  current_stock: 0,
});

export default function CreateIndent() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);
  const [unitsData, setUnitsData] = useState([]);

  const [formData, setFormData] = useState({
    department_id: "",
    department_name: "",
    priority: "",
    remarks: "",
  });

  const [items, setItems] = useState([emptyRow(1)]);
  const [errors, setErrors] = useState({});

  const handleError = (msg) =>
    toast({ title: "Error", description: msg, variant: "destructive" });
  const handleSuccess = (msg) => toast({ title: "Success", description: msg });

  /* ---------------- unit helpers ---------------- */

  const unitMap = useMemo(() => {
    const m = {};
    (unitsData || []).forEach((u) => {
      m[String(u.primary_id)] = u;
    });
    return m;
  }, [unitsData]);

  // Base unit ka conversion_to_base_unit = 1 hota hai (0 nahi)
  const convFactor = useCallback(
    (unitId) => {
      const f = num(unitMap[String(unitId)]?.conversion_to_base_unit, 1);
      return f > 0 ? f : 1;
    },
    [unitMap],
  );

  const unitLabel = useCallback(
    (unitId) => {
      const u = unitMap[String(unitId)];
      return u?.short_code_name || u?.unit_name || "";
    },
    [unitMap],
  );

  const toBase = useCallback(
    (qty, unitId) => num(qty) * convFactor(unitId),
    [convFactor],
  );

  // Ingredient ke base unit ki family ke hi units (Atta pe ML nahi)
  const unitsForItem = useCallback(
    (item) => {
      const anchor = unitMap[String(item.default_unit_id)];
      if (!anchor) return [];
      const parentId =
        Number(anchor.parent_unit_id) || Number(anchor.primary_id);

      return (unitsData || [])
        .filter((u) => Number(u.parent_unit_id) === parentId)
        .sort(
          (a, b) =>
            num(a.conversion_to_base_unit, 1) -
            num(b.conversion_to_base_unit, 1),
        )
        .map((u) => ({
          value: String(u.primary_id),
          label: `${u.unit_name} (${u.short_code_name || u.unit_name})`,
        }));
    },
    [unitMap, unitsData],
  );

  /* ---------------- pricing ---------------- */

  /**
   * IMPORTANT
   * Backend me saare rate PER BASE UNIT hote hain (per gram / per ml).
   * Isliye:
   *    qty_base   = qty x conversion_to_base_unit(selected unit)
   *    line total = qty_base x rate_per_base
   *
   * Purana code qty ko default_unit (KG) me convert karke per-gram rate se
   * guna karta tha — 1000x galat aata tha.
   */
  const rowCalc = useCallback(
    (item) => {
      const qty = num(item.quantity);
      const qtyBase = toBase(qty, item.unit_id);
      const ratePerBase = num(item.rate_per_base);

      const ratePerSelectedUnit = ratePerBase * convFactor(item.unit_id);
      const lineTotal = qtyBase * ratePerBase;

      return { qty, qtyBase, ratePerBase, ratePerSelectedUnit, lineTotal };
    },
    [toBase, convFactor],
  );

  const grandTotal = useMemo(
    () => items.reduce((sum, it) => sum + rowCalc(it).lineTotal, 0),
    [items, rowCalc],
  );

  /* ---------------- options ---------------- */

  const departmentOptions = useMemo(() => {
    const allowed = (retrieveData("department_ids") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    return (departments || [])
      .filter(
        (d) => allowed.length === 0 || allowed.includes(String(d.department_id)),
      )
      .map((d) => ({
        value: String(d.department_id),
        label: d.department_name,
      }));
  }, [departments]);

  // Ingredient list — abhi saare dikhte hain. Department wise chahiye to
  // niche wali filter line uncomment kar dena.
  const ingredientOptions = useMemo(
    () =>
      (allIngredients || [])
        // .filter((ing) => String(ing.department_id) === String(formData.department_id))
        .map((ing) => ({
          value: String(ing.primary_id),
          label: ing.ingredient_name || "Unnamed Item",
          label2: ing.ingredient_alter_name || "",
          ingredient_id: ing.primary_id,
          default_unit_id: ing.default_unit_id || "",
          // rate per BASE unit
          rate_per_base:
            num(ing.avg_landed_price) ||
            num(ing.last_landed_price) ||
            num(ing.avg_price) ||
            num(ing.last_price),
          current_stock: num(ing.balance_stock),
        })),
    [allIngredients],
  );

  /* ---------------- handlers ---------------- */

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "department_id") {
        const dept = departments.find(
          (d) => String(d.department_id) === String(value),
        );
        next.department_name = dept?.department_name || "";
      }

      return next;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const updated = { ...item };

        if (field === "itemName") {
          const sel = ingredientOptions.find((o) => o.value === value);
          updated.itemName = value;

          if (sel) {
            updated.ingredient_id = sel.ingredient_id;
            updated.default_unit_id = sel.default_unit_id;
            updated.unit_id = String(sel.default_unit_id || "");
            updated.rate_per_base = sel.rate_per_base;
            updated.current_stock = sel.current_stock;
            updated.quantity = "";
          } else {
            return emptyRow(item.id);
          }
        } else if (field === "quantity") {
          if (value !== "" && !/^\d*\.?\d{0,4}$/.test(value)) return item;
          updated.quantity = value;
        } else if (field === "unit_id") {
          // Unit badla -> quantity ko nayi unit me convert karo (mitao mat)
          const oldF = convFactor(item.unit_id);
          const newF = convFactor(value);
          const qty = num(item.quantity);

          if (qty > 0 && oldF !== newF) {
            updated.quantity = String(
              parseFloat(((qty * oldF) / newF).toFixed(6)),
            );
          }
          updated.unit_id = value;
        } else {
          updated[field] = value;
        }

        return updated;
      }),
    );

    if (errors[`item_${id}_${field}`]) {
      setErrors((prev) => ({ ...prev, [`item_${id}_${field}`]: "" }));
    }
    if (errors.items) {
      setErrors((prev) => ({ ...prev, items: "" }));
    }
  };

  const addNewItem = () => {
    const newId = Math.max(...items.map((i) => i.id), 0) + 1;
    setItems((prev) => [...prev, emptyRow(newId)]);
  };

  const removeItem = (id) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`item_${id}_itemName`];
      delete next[`item_${id}_quantity`];
      delete next[`item_${id}_unit_id`];
      return next;
    });
  };

  /* ---------------- validation ---------------- */

  const validateForm = () => {
    const e = {};

    if (!formData.department_id) e.department_id = "Department is required";
    if (!formData.priority) e.priority = "Priority is required";

    let validCount = 0;
    const seen = new Set();

    items.forEach((item) => {
      const touched = item.itemName || item.quantity || item.unit_id;
      if (!touched) return;

      if (!item.itemName) e[`item_${item.id}_itemName`] = "Item required";
      if (num(item.quantity) <= 0)
        e[`item_${item.id}_quantity`] = "Enter a quantity";
      if (!item.unit_id) e[`item_${item.id}_unit_id`] = "Unit required";

      if (item.itemName && num(item.quantity) > 0 && item.unit_id) {
        if (seen.has(item.ingredient_id)) {
          e[`item_${item.id}_itemName`] = "Already added above";
        } else {
          seen.add(item.ingredient_id);
          validCount += 1;
        }
      }
    });

    if (validCount === 0) e.items = "Add at least one complete item";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- submit ---------------- */

  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstError = document.querySelector('[data-error="true"]');
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      handleError("Please fix the highlighted fields");
      return;
    }

    setShowLoaderAdmin(true);

    const itemsForSave = items
      .filter((i) => i.itemName && num(i.quantity) > 0 && i.unit_id)
      .map((i) => {
        const c = rowCalc(i);
        return {
          ingredient_id: i.ingredient_id,
          requested_qty: c.qty,
          unit_id: Number(i.unit_id),
          unit_name: unitLabel(i.unit_id),
          // rate per selected unit (backend base me convert karega)
          price: parseFloat(c.ratePerSelectedUnit.toFixed(6)),
          total_price: parseFloat(c.lineTotal.toFixed(4)),
        };
      });

    console.log("kitchen_raise_item_request payload:", itemsForSave);

    const fd = new FormData();
    fd.append("items", JSON.stringify(itemsForSave));
    fd.append("priority", formData.priority);
    fd.append("remarks", formData.remarks || "");
    fd.append("department_id", formData.department_id);
    fd.append("department_name", formData.department_name);
    fd.append("total_amount", grandTotal.toFixed(2));

    try {
      const response = await server_post_data(kitchen_raise_item_request, fd);

      if (response.data.error) {
        handleError(response.data.message || "Failed to save indent");
      } else {
        handleSuccess(
          `Indent saved. Request No: ${response.data.request_no || ""}`,
        );
        navigate("/indents");
      }
    } catch (error) {
      console.error("Save error:", error);
      handleError("Network error. Please try again.");
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  const handleCancel = () => {
    const dirty =
      formData.department_id ||
      formData.priority ||
      formData.remarks ||
      items.some((i) => i.itemName || i.quantity);

    if (dirty && !window.confirm("Discard changes?")) return;
    navigate("/indents");
  };

  /* ---------------- data ---------------- */

  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_department_with_categories, fd);
      if (!res.data.error) setDepartments(res.data.message?.data || []);
    } catch (e) {
      console.error("departments:", e);
    }
  }, []);

  const fetchIngredients = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_view_ingredients, fd);
      if (!res.data.error && res.data.message?.ingredients_data) {
        setAllIngredients(res.data.message.ingredients_data);
      }
    } catch (e) {
      console.error("ingredients:", e);
      handleError("Failed to load ingredients");
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_view_units, fd);
      if (!res.data.error && res.data.message) {
        setUnitsData(res.data.message || []);
      }
    } catch (e) {
      console.error("units:", e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setShowLoaderAdmin(true);
      await Promise.all([
        fetchDepartments(),
        fetchIngredients(),
        fetchUnits(),
      ]);
      setShowLoaderAdmin(false);
    })();
  }, [fetchDepartments, fetchIngredients, fetchUnits]);

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header
        title="Create New Indent"
        subtitle="Raise a new material request"
      />

      <div className="p-6">
        <div className="mx-auto space-y-6">
          <Button variant="ghost" onClick={handleCancel} className="gap-2 -ml-2">
            <ArrowLeft className="w-4 h-4" /> Back to Indents
          </Button>

          <div className="rounded-xl border bg-card p-6 space-y-6">
            {/* ---------- Header ---------- */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Indent Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>
                    Department <span className="text-destructive">*</span>
                  </Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={formData.department_id}
                    onValueChange={(v) => handleFormChange("department_id", v)}
                    placeholder="Select Department"
                    className={cn(errors.department_id && "border-destructive")}
                  />
                  {errors.department_id && (
                    <p className="text-sm text-destructive" data-error="true">
                      {errors.department_id}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Stock will be transferred to this department
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>
                    Priority <span className="text-destructive">*</span>
                  </Label>
                  <SearchableSelect
                    options={priorities}
                    value={formData.priority}
                    onValueChange={(v) => handleFormChange("priority", v)}
                    placeholder="Select Priority"
                    className={cn(errors.priority && "border-destructive")}
                  />
                  {errors.priority && (
                    <p className="text-sm text-destructive" data-error="true">
                      {errors.priority}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* ---------- Items ---------- */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Items to Indent</h3>
                <Button onClick={addNewItem} className="gap-2">
                  <Plus className="w-4 h-4" /> Add Item
                </Button>
              </div>

              {errors.items && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive" data-error="true">
                    {errors.items}
                  </p>
                </div>
              )}

              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Item Name *</TableHead>
                      <TableHead className="text-center">Quantity *</TableHead>
                      <TableHead>Unit *</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total Price</TableHead>
                      <TableHead className="w-10">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => {
                      const c = rowCalc(item);
                      const unitOpts = unitsForItem(item);
                      const uLabel = unitLabel(item.unit_id);
                      const baseLabel = unitLabel(
                        unitMap[String(item.unit_id)]?.parent_unit_id,
                      );

                      return (
                        <TableRow key={item.id}>
                          <TableCell>{idx + 1}</TableCell>

                          {/* Item */}
                          <TableCell className="min-w-56">
                            <SearchableSelect
                              options={ingredientOptions}
                              value={item.itemName}
                              onValueChange={(v) =>
                                handleItemChange(item.id, "itemName", v)
                              }
                              placeholder={
                                ingredientOptions.length === 0
                                  ? "Loading ingredients..."
                                  : "Select Ingredient"
                              }
                              className={cn(
                                errors[`item_${item.id}_itemName`] &&
                                  "border-destructive",
                              )}
                            />
                            {errors[`item_${item.id}_itemName`] && (
                              <p
                                className="text-xs text-destructive mt-1"
                                data-error="true"
                              >
                                {errors[`item_${item.id}_itemName`]}
                              </p>
                            )}
                          </TableCell>

                          {/* Quantity */}
                          <TableCell className="min-w-32">
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder="0"
                              value={item.quantity}
                              disabled={!item.itemName}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              className={cn(
                                "text-center",
                                errors[`item_${item.id}_quantity`] &&
                                  "border-destructive",
                              )}
                            />
                            {c.qty > 0 && baseLabel && baseLabel !== uLabel && (
                              <p className="text-[11px] text-muted-foreground mt-1 text-center">
                                = {fmtQty(c.qtyBase)} {baseLabel}
                              </p>
                            )}
                            {errors[`item_${item.id}_quantity`] && (
                              <p
                                className="text-xs text-destructive mt-1"
                                data-error="true"
                              >
                                {errors[`item_${item.id}_quantity`]}
                              </p>
                            )}
                          </TableCell>

                          {/* Unit */}
                          <TableCell className="min-w-40">
                            <SearchableSelect
                              options={unitOpts}
                              value={String(item.unit_id || "")}
                              onValueChange={(v) =>
                                handleItemChange(item.id, "unit_id", v)
                              }
                              placeholder={
                                !item.itemName
                                  ? "Select item first"
                                  : "Select unit"
                              }
                              disabled={!item.itemName}
                              className={cn(
                                errors[`item_${item.id}_unit_id`] &&
                                  "border-destructive",
                              )}
                            />
                            {errors[`item_${item.id}_unit_id`] && (
                              <p className="text-xs text-destructive mt-1">
                                {errors[`item_${item.id}_unit_id`]}
                              </p>
                            )}
                          </TableCell>

                          {/* Unit Price */}
                          <TableCell className="text-right">
                            {c.ratePerBase > 0 ? (
                              <>
                                <span className="font-medium">
                                  {money(c.ratePerSelectedUnit)}
                                </span>
                                <p className="text-[11px] text-muted-foreground">
                                  per {uLabel}
                                </p>
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                No rate yet
                              </span>
                            )}
                          </TableCell>

                          {/* Total */}
                          <TableCell className="text-right font-semibold">
                            {c.lineTotal > 0 ? money(c.lineTotal) : "—"}
                          </TableCell>

                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              disabled={items.length === 1}
                              className="text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end p-4 bg-muted/30 rounded-lg border">
                <div className="flex justify-between gap-4 font-semibold text-lg min-w-48">
                  <span>Estimated Total:</span>
                  <span className="text-primary">{money(grandTotal)}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Rates come from each ingredient's average landed cost. The final
                amount is set when the store actually issues the material.
              </p>
            </div>

            <div className="border-t border-border" />

            <div className="space-y-2">
              <Label>Remarks / Special Instructions</Label>
              <Textarea
                placeholder="Any notes..."
                value={formData.remarks}
                onChange={(e) => handleFormChange("remarks", e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={showLoaderAdmin}>
              {showLoaderAdmin ? "Saving..." : "Submit Indent"}
            </Button>
          </div>
        </div>
      </div>

      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}